import React, { useState, useCallback } from 'react';
import { priceBenchmarkApi } from '../api/client';
import { BlockView, Block, CostEstimator } from './PriceBenchmarkTab';
import StageGuide from '../components/StageGuide';
import { markOnboardingStep } from '../lib/onboarding';

/* ============================================================================
 * Price Benchmark Explorer — standalone market-research tool (Phase 2)
 * Lives under the Research sidebar group. Unlike the in-context Acquisition
 * sub-tab (which auto-scopes to an opportunity's NAICS/PSC), this is a free-form
 * explorer: the user types any NAICS and/or PSC code to see what agencies have
 * paid. Reuses the same /price-benchmark endpoint and the BlockView display
 * component so results look identical to the in-context view.
 * ==========================================================================*/

const input: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%',
};
const label: React.CSSProperties = {
  fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'block',
};

export default function PriceBenchmarkExplorer() {
  const [naics, setNaics] = useState('');
  const [psc, setPsc] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [naicsBlock, setNaicsBlock] = useState<Block | null>(null);
  const [pscBlock, setPscBlock] = useState<Block | null>(null);
  // Codes locked in at search time — the estimator grounds on these, not the
  // live inputs (which the user may edit after searching).
  const [searchedNaics, setSearchedNaics] = useState('');
  const [searchedPsc, setSearchedPsc] = useState('');

  const run = useCallback(async () => {
    const n = naics.trim();
    const p = psc.trim();
    if (!n && !p) {
      setError('Enter a NAICS or PSC code to benchmark.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await priceBenchmarkApi.get({ naics: n, psc: p, from: yearFrom, to: yearTo });
      setNaicsBlock(res.naics || null);
      setPscBlock(res.psc || null);
      setSearchedNaics(n);
      setSearchedPsc(p);
      setSearched(true);
      markOnboardingStep('price_benchmark');
    } catch (e: any) {
      setError(e?.message || 'Failed to load price benchmark');
    } finally {
      setLoading(false);
    }
  }, [naics, psc, yearFrom, yearTo]);

  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') run(); };

  return (
    <div>
      <StageGuide id="price-benchmark" />
      <div style={{ marginBottom: 8 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>Price Benchmark</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
          Market research: see what federal agencies have paid for work in a given NAICS or PSC code.
          Enter a code to explore historical prime-award obligations.
        </p>
      </div>

      {/* Search controls */}
      <div style={{ background: 'white', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={label}>NAICS code</label>
            <input value={naics} onChange={e => setNaics(e.target.value.replace(/\D/g, '').slice(0, 6))}
                   onKeyDown={onKey} placeholder="e.g. 541519" style={input} />
          </div>
          <div>
            <label style={label}>PSC code</label>
            <input value={psc} onChange={e => setPsc(e.target.value.toUpperCase().slice(0, 4))}
                   onKeyDown={onKey} placeholder="e.g. 7B22" style={input} />
          </div>
          <div>
            <label style={label}>Year from</label>
            <input value={yearFrom} onChange={e => setYearFrom(e.target.value.replace(/\D/g, '').slice(0, 4))}
                   onKeyDown={onKey} placeholder="all" style={{ ...input, width: 70 }} />
          </div>
          <div>
            <label style={label}>Year to</label>
            <input value={yearTo} onChange={e => setYearTo(e.target.value.replace(/\D/g, '').slice(0, 4))}
                   onKeyDown={onKey} placeholder="all" style={{ ...input, width: 70 }} />
          </div>
          <button onClick={run} disabled={loading}
                  style={{ padding: '9px 20px', background: loading ? '#a5b4fc' : '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer', height: 38 }}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', fontSize: 11, color: '#92400e', marginBottom: 16 }}>
        Figures are historical prime-award obligations from federal award data, grouped by code. The distribution (median / percentiles) is computed across all years; the year filter trims only the yearly chart. Award dollars vary widely by scope, so treat these as directional benchmarks, not fixed prices.
      </div>

      {error && <div style={{ fontSize: 13, color: '#dc2626', padding: 12 }}>{error}</div>}

      {!searched && !loading && !error && (
        <div style={{ background: 'white', borderRadius: 8, padding: 40, border: '1px dashed #d1d5db', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: '0 0 4px' }}>Enter a NAICS or PSC code</p>
          <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
            See median, percentile spread, top buying agencies, and award history for that code.
          </p>
        </div>
      )}

      {searched && !loading && !error && (
        <>
          {searchedNaics && naicsBlock && <BlockView b={naicsBlock} />}
          {searchedPsc && pscBlock && <BlockView b={pscBlock} />}
          {(searchedNaics || searchedPsc) && (
            <CostEstimator naics={searchedNaics} psc={searchedPsc} />
          )}
        </>
      )}
    </div>
  );
}
